<script setup>
import { ref } from 'vue'
import { usePwa } from '../composables/usePwa'
import { t } from '../lib/i18n'
import Icon from './Icon.vue'

const { canInstall, showIosHint, needsRefresh, install, dismiss, refresh } = usePwa()

const iosSteps = ref(false)
const installing = ref(false)

async function onInstall() {
  installing.value = true
  try {
    await install()
  } finally {
    installing.value = false
  }
}
</script>

<template>
  <!-- Ny versjon tilgjengelig -->
  <Transition name="slide">
    <div v-if="needsRefresh" class="update" role="status">
      <Icon name="sparkles" :size="22" class="update__icon" />
      <div class="grow">
        <p class="update__title">{{ t('Ny versjon klar') }}</p>
        <p class="update__text">{{ t('Trykk for å oppdatere appen.') }}</p>
      </div>
      <button class="update__btn" @click="refresh">{{ t('Oppdater') }}</button>
    </div>
  </Transition>

  <!-- Forslag om å installere -->
  <Transition name="slide">
    <div v-if="canInstall || showIosHint" class="install">
      <div class="row" style="align-items: flex-start">
        <span class="install__icon"><Icon name="star" :size="21" /></span>
        <div class="grow">
          <p class="install__title">{{ t('Legg Bonto på hjemskjermen') }}</p>
          <p class="install__text">
            {{ t('Da åpnes den som en vanlig app, uten nettleserlinje.') }}
          </p>
        </div>
        <button class="install__close" :aria-label="t('Ikke nå')" @click="dismiss">
          <Icon name="close" :size="18" />
        </button>
      </div>

      <!-- Safari har ingen installasjonsdialog; brukeren må gjøre det selv. -->
      <div v-if="showIosHint">
        <button v-if="!iosSteps" class="btn btn--sm btn--block" style="margin-top: 12px"
                @click="iosSteps = true">
          {{ t('Vis meg hvordan') }}
        </button>
        <ol v-else class="steps">
          <li>{{ t('Trykk på') }} <strong>{{ t('Del') }}</strong>{{ t('-knappen nederst') }} <span class="share">􀈂</span></li>
          <li>{{ t('Bla ned og velg') }} <strong>{{ t('Legg til på Hjem-skjerm') }}</strong></li>
          <li>{{ t('Trykk') }} <strong>{{ t('Legg til') }}</strong></li>
        </ol>
      </div>

      <div v-else class="row" style="gap: 8px; margin-top: 12px">
        <button class="btn btn--ghost btn--sm" @click="dismiss">{{ t('Ikke nå') }}</button>
        <button class="btn btn--sm grow" :disabled="installing" @click="onInstall">
          {{ t('Installer') }}
        </button>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
/* Begge legger seg over bunnavigasjonen, ikke oppå innholdet. */
.update, .install {
  position: fixed;
  left: 12px;
  right: 12px;
  bottom: calc(var(--nav-h) + var(--safe-b) + 12px);
  z-index: 75;
  max-width: 460px;
  margin: 0 auto;
  border-radius: 16px;
  box-shadow: var(--shadow-lg);
}

.update {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: var(--brand);
  color: var(--on-accent);
}
.update__icon { flex: none; }
.update__title { font-weight: 700; font-size: 0.95rem; }
.update__text { font-size: 0.8rem; opacity: 0.85; }
.update__btn {
  flex: none;
  min-height: 38px;
  padding: 0 16px;
  border: 0;
  border-radius: 10px;
  background: var(--on-accent);
  color: var(--brand);
  font-weight: 700;
  cursor: pointer;
}
.update__btn:active { transform: scale(0.96); }

.install {
  padding: 14px;
  background: var(--surface);
  border: 1px solid var(--border);
}
.install__icon {
  width: 40px; height: 40px; flex: none;
  display: flex; align-items: center; justify-content: center;
  border-radius: 12px;
  background: var(--brand-soft);
  color: var(--brand);
}
.install__title { font-weight: 700; font-size: 0.95rem; }
.install__text { font-size: 0.82rem; color: var(--muted); margin-top: 2px; }
.install__close {
  flex: none;
  display: flex; align-items: center; justify-content: center;
  width: 30px; height: 30px;
  border: 0; border-radius: 8px;
  background: transparent; color: var(--muted);
  cursor: pointer;
}

.steps {
  margin: 12px 0 0;
  padding-left: 20px;
  font-size: 0.85rem;
  line-height: 1.7;
}
.share { font-family: system-ui; }

.slide-enter-active, .slide-leave-active { transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
.slide-enter-from, .slide-leave-to { opacity: 0; transform: translateY(16px); }
</style>
